import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { G, styles, api } from './theme.js';
import { Btn, Toast, Av, Spinner, Badge, Dot } from './ui.jsx';
import { Auth } from './screens/Auth.jsx';
import { SetupWizard } from './screens/SetupWizard.jsx';
import { SetupCompletenessPill } from './widgets/SetupCompletenessPill.jsx';
import { Dashboard } from './screens/Dashboard.jsx';
import { Contacts } from './screens/Contacts.jsx';
import { Jobs } from './screens/Jobs.jsx';
import { Estimates } from './screens/Estimates.jsx';
import { Invoices } from './screens/Invoices.jsx';
import { Team } from './screens/Team.jsx';
import { Settings } from './screens/Settings.jsx';
import { Training } from './screens/Training.jsx';
import { ClaudeConnector } from './screens/ClaudeConnector.jsx';
import { Coach } from './screens/Coach.jsx';
import { SystemHealth } from './screens/SystemHealth.jsx';
import { ContentStudio } from './screens/ContentStudio.jsx';
import { ActivityLog } from './screens/ActivityLog.jsx';
import { AgencyPricing } from './screens/AgencyPricing.jsx';
import { AgencyInbox } from './screens/AgencyInbox.jsx';
import { AiCredits } from './screens/AiCredits.jsx';
import { WebsiteBuilder } from './screens/WebsiteBuilder.jsx';
import { AgencyBranding } from './screens/AgencyBranding.jsx';
import { WorkflowBuilder } from './screens/WorkflowBuilder.jsx';
import { RankAndRent } from './screens/RankAndRent.jsx';
import { EmailTemplates } from './screens/EmailTemplates.jsx';
import { JobsMap } from './screens/JobsMap.jsx';
import { Migration } from './screens/Migration.jsx';
import { Performance } from './screens/Performance.jsx';
import { CsmPortal } from './screens/CsmPortal.jsx';
import { LeadSources } from './screens/LeadSources.jsx';
import { Referrals } from './screens/Referrals.jsx';
import { Integrations } from './screens/Integrations.jsx';
import { Workflows } from './screens/Workflows.jsx';
import { Messaging } from './screens/Messaging.jsx';
import { Financials } from './screens/Financials.jsx';
import { PriceBook } from './screens/PriceBook.jsx';
import { Memberships } from './screens/Memberships.jsx';
import { Timesheets } from './screens/Timesheets.jsx';
import { Calendar } from './screens/Calendar.jsx';
import { Pipeline } from './screens/Pipeline.jsx';
import { Segments } from './screens/Segments.jsx';
import { PowerDialer } from './screens/PowerDialer.jsx';
import { LeadMarketplace } from './screens/LeadMarketplace.jsx';
import { TeamChat } from './screens/TeamChat.jsx';
import { Documents } from './screens/Documents.jsx';
import { Equipment } from './screens/Equipment.jsx';
import { Inventory } from './screens/Inventory.jsx';
import { Marketing } from './screens/Marketing.jsx';
import { Agents } from './screens/Agents.jsx';
import { Forms } from './screens/Forms.jsx';
import { Agency } from './screens/Agency.jsx';
import { TechApp } from './screens/TechApp.jsx';
import { Issues } from './screens/Issues.jsx';
import { QuickCreateFab } from './widgets/QuickCreateFlow.jsx';
import { Permits } from './screens/Permits.jsx';
import { Growth } from './screens/Growth.jsx';
import { Billing } from './screens/Billing.jsx';
import { LeadMap } from './screens/LeadMap.jsx';
import { Reviews } from './screens/Reviews.jsx';
import { InvoicePay } from './screens/InvoicePay.jsx';
import { EstimateView } from './screens/EstimateView.jsx';
import { DocumentSign } from './screens/DocumentSign.jsx';
import { BookingWidget } from './screens/BookingWidget.jsx';
import { MarketingLanding, LegalPage } from './screens/Marketing.public.jsx';
import { VerifyEmail, ForgotPassword, ResetPassword } from './screens/AuthPages.jsx';
import { HelpWidget } from './widgets/HelpWidget.jsx';
import { TrainingWidget } from './widgets/TrainingWidget.jsx';
import { OnboardingWizard } from './widgets/OnboardingWizard.jsx';
import { FirstRunWizard } from './widgets/FirstRunWizard.jsx';
import { OnboardingForm } from './widgets/OnboardingForm.jsx';
import { ContactQuickEditProvider } from './widgets/ContactQuickEdit.jsx';
import { VoipDialerProvider } from './widgets/VoipDialer.jsx';
import { CmdK } from './widgets/CmdK.jsx';
import { ContactProfile } from './screens/ContactProfile.jsx';
import { ErrorBoundary, Safe } from './widgets/ErrorBoundary.jsx';

// Global mount: any screen can fire `tradeops:open-contact` with { id } or { contactId }.
// Opens the profile as a MODAL OVERLAY inside the app shell so the user can still
// see + click the sidebar. Clicking any sidebar item closes the profile and switches
// to the new screen — handled by a separate effect listening for `tradeops:tab-change`.
function GlobalContactProfile({ toast }) {
  const [modalId, setModalId] = React.useState(null);
  React.useEffect(() => {
    const open = (e) => {
      const id = e.detail?.id || e.detail?.contactId;
      if (id) setModalId(id);
    };
    const close = () => setModalId(null);
    window.addEventListener('tradeops:open-contact', open);
    // When the user navigates via the sidebar, close any open contact profile so
    // they actually see the new screen instead of being stuck behind a modal.
    window.addEventListener('tradeops:tab-change', close);
    return () => {
      window.removeEventListener('tradeops:open-contact', open);
      window.removeEventListener('tradeops:tab-change', close);
    };
  }, []);
  if (!modalId) return null;
  return <ContactProfile contactId={modalId} onClose={()=>setModalId(null)} toast={toast}/>;
}

// Advisor-Jetpack / GHL navigation pattern: the left sidebar lists ~10
// top-level DOMAINS only. When the user picks a domain, the content area
// shows a horizontal sub-tab strip with that domain's children. Reduces
// cognitive load and matches what owners are used to from GHL.
//
// Each NAV entry has a `domain` (left-sidebar bucket). The first entry per
// domain is the domain's default tab — what we route to when the sidebar
// item is clicked.
const NAV = [
  // ── AGENCY (super-admin only) ──
  { id:'agency',          label:'Console',           domain:'agency', superAdminOnly:true },
  { id:'inbox',           label:'Agency Inbox',      domain:'agency', superAdminOnly:true },
  { id:'agency-pricing',  label:'Platform Pricing',  domain:'agency', superAdminOnly:true },
  { id:'agency-branding', label:'White-label',       domain:'agency', superAdminOnly:true },
  { id:'rank-and-rent',   label:'Rank & Rent Sites', domain:'agency', superAdminOnly:true },
  // ── DASHBOARD ──
  { id:'dashboard',          label:'Overview',           domain:'dashboard' },
  { id:'financials',         label:'Financials',         domain:'dashboard' },
  { id:'performance',        label:'Performance',        domain:'dashboard' },
  { id:'csm-portal',         label:'CSM Numbers',        domain:'dashboard' },
  { id:'marketing-overview', label:'Campaigns',          domain:'dashboard' },
  { id:'lead-sources',       label:'Lead Sources',       domain:'dashboard' },
  { id:'leadmap',            label:'Lead Map',           domain:'dashboard' },
  // ── CONVERSATIONS ──
  { id:'messaging',          label:'Inbox',              domain:'conversations' },
  { id:'coach',              label:'AI Coach',           domain:'conversations' },
  { id:'agents',             label:'AI Agents',          domain:'conversations' },
  // ── CONTACTS ──
  { id:'contacts',           label:'All Contacts',       domain:'contacts' },
  { id:'pipeline',           label:'Pipeline',           domain:'contacts' },
  { id:'segments',           label:'Segments',           domain:'contacts' },
  { id:'power-dialer',       label:'Power Dialer',       domain:'contacts' },
  { id:'forms',              label:'Forms',              domain:'contacts' },
  // ── GET LEADS (own sidebar bucket for high visibility) ──
  { id:'marketplace',        label:'Get New Leads',      domain:'leads-market' },
  // ── JOBS ──
  { id:'jobs',               label:'Dispatch',           domain:'jobs' },
  { id:'calendar',           label:'Calendar',           domain:'jobs' },
  { id:'jobsmap',            label:'Map',                domain:'jobs' },
  { id:'issues',             label:'Issues',             domain:'jobs' },
  // ── REVENUE ──
  { id:'estimates',          label:'Estimates',          domain:'revenue' },
  { id:'invoices',           label:'Invoices',           domain:'revenue' },
  { id:'memberships',        label:'Memberships',        domain:'revenue' },
  { id:'pricebook',          label:'Price Book',         domain:'operations' },
  // ── MARKETING ──
  { id:'marketing',          label:'Channels',           domain:'marketing' },
  { id:'website',            label:'Website',            domain:'marketing' },
  { id:'workflows',          label:'Workflows',          domain:'marketing' },
  { id:'email-templates',    label:'Email Templates',    domain:'marketing' },
  { id:'reviews',            label:'Reviews',            domain:'marketing' },
  { id:'referrals',          label:'Referrals',          domain:'marketing' },
  { id:'growth',             label:'Growth Engine',      domain:'marketing' },
  // ── OPERATIONS ──
  { id:'documents',          label:'Documents',          domain:'operations' },
  { id:'permits',            label:'Permits',            domain:'operations' },
  { id:'equipment',          label:'Equipment',          domain:'operations' },
  { id:'inventory',          label:'Inventory',          domain:'operations' },
  { id:'timesheets',         label:'Timesheets',         domain:'operations' },
  { id:'training',           label:'Training',           domain:'operations' },
  // ── TEAM ──
  { id:'team',               label:'Team',               domain:'team' },
  // ── CHAT (top-level sidebar, always visible) ──
  { id:'team-chat',          label:'Team Chat',          domain:'chat' },
  // ── SETTINGS ──
  { id:'settings',           label:'Account',            domain:'settings' },
  { id:'integrations',       label:'Integrations',       domain:'settings' },
  { id:'billing',            label:'Billing',            domain:'settings' },
  { id:'ai-credits',         label:'AI Credits',         domain:'settings' },
  { id:'claude',             label:'Claude Connector',   domain:'settings' },
  { id:'system-health',      label:'System Health',      domain:'settings' },
  { id:'activity',           label:'Activity Log',       domain:'settings' },
  { id:'migration',          label:'Migrate Data',       domain:'settings' },
];

// Top-level sidebar domains. Order = how they appear on the left.
const DOMAINS = [
  { id:'agency',        label:'Agency',        icon:'⚡', superAdminOnly:true },
  { id:'dashboard',     label:'Dashboard',     icon:'🏠' },
  { id:'leads-market',  label:'Get Leads',     icon:'💰' },
  { id:'conversations', label:'Conversations', icon:'💬' },
  { id:'contacts',      label:'Contacts',      icon:'👥' },
  { id:'jobs',          label:'Jobs',          icon:'🔧' },
  { id:'revenue',       label:'Payments',      icon:'💵' },
  { id:'marketing',     label:'Marketing',     icon:'📣' },
  { id:'operations',    label:'Operations',    icon:'📦' },
  { id:'team',          label:'Team',          icon:'🧑‍🔧' },
  { id:'chat',          label:'Chat',          icon:'💬' },
  { id:'settings',      label:'Settings',      icon:'⚙️' },
];

// Legacy NAV_GROUPS kept as an empty stub — removed in this restructure.
const NAV_GROUPS = [];

// Top-level routing. Public unauthenticated paths render before the auth shell.
// Wrap everything in an error boundary so a single broken screen never blanks
// the whole app. ErrorBoundary catches render errors + reports to System Health.
function AppShell() {
  const p = window.location.pathname;
  if (p.startsWith('/contact/')) {
    const id = p.split('/contact/')[1].split('/')[0].split('?')[0];
    return <AuthedApp initialPath={p} contactPageId={id}/>;
  }
  if (p.startsWith('/pay/')) return <InvoicePay/>;
  if (p.startsWith('/estimate/') || p.startsWith('/portal/estimate/')) return <EstimateView/>;
  if (p.startsWith('/document/')) return <DocumentSign/>;
  if (p.startsWith('/book/')) return <BookingWidget/>;
  if (p === '/terms') return <LegalPage kind="terms"/>;
  if (p === '/privacy') return <LegalPage kind="privacy"/>;
  if (p === '/verify-email') return <VerifyEmail/>;
  if (p === '/forgot-password') return <ForgotPassword/>;
  if (p === '/reset-password') return <ResetPassword/>;
  return <AuthedApp initialPath={p}/>;
}

export default function App() {
  return <ErrorBoundary><AppShell/></ErrorBoundary>;
}

function AuthedApp({ initialPath = '/', contactPageId = null }) {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  // Mobile: sidebar collapses behind hamburger
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 900);
  useEffect(() => {
    const onResize = () => { if (typeof window !== 'undefined' && window.innerWidth >= 900) setSidebarOpen(true); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // /login + /signup show Auth shell directly. /, /pricing show marketing for unauthenticated.
  const wantsAuthShell = initialPath === '/login' || initialPath === '/signup';
  const wantsMarketing = (initialPath === '/' || initialPath === '/pricing');
  const [tab, setTab] = useState('dashboard');
  const [toastMsg, setToastMsg] = useState(null);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [onbDismissed, setOnbDismissed] = useState(() => {
    try { return localStorage.getItem('tradeops-onboarding-dismissed') === '1'; } catch { return false; }
  });
  useEffect(() => { try { if (onbDismissed) localStorage.setItem('tradeops-onboarding-dismissed','1'); } catch {} }, [onbDismissed]);

  useEffect(() => {
    api('/api/auth/user').then(u => {
      setUser(u);
      setTab(u.role === 'super_admin' && !u.accountId ? 'agency' : 'dashboard');
      // Cache vertical-agnostic terminology so useTerminology() returns the
      // right words across every screen (Tech vs Agent vs Stylist, etc).
      try {
        const { setTerminology } = require('./hooks/useTerminology.js');
        setTerminology(u?.account?.terminology || null);
      } catch {}
      // Register for native push notifications (no-op when running in plain browser)
      try { import('./native.js').then(n => n.registerPushNotifications(api).catch(()=>{})); } catch {}
    }).catch(()=>{}).finally(()=>setAuthReady(true));
  }, []);

  // Re-broadcast a focus event whenever the tab regains visibility. Screens
  // listen to 'tradeops:tab-focus' and reload their own data. Fixes the
  // "blank page on tab switch" issue where stale stateful screens didn't
  // refresh after the SW served them from cache.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) return;
      window.dispatchEvent(new CustomEvent('tradeops:tab-focus'));
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
  }, []);

  const [features, setFeatures] = useState({});
  useEffect(() => { if (user) { refreshIntegrationStatus(); api('/api/admin-panel/features/me').then(d => setFeatures(d.effective||{})).catch(()=>{}); }}, [user]);
  const refreshIntegrationStatus = () => api('/api/integrations/status').then(setIntegrationStatus).catch(()=>{});

  const toast = (msg, type='success', opts={}) => {
    // opts: { action: 'Undo', onAction: () => {...}, durationMs: 8000 }
    setToastMsg({ msg, type, action: opts.action, onAction: opts.onAction });
    setTimeout(()=>setToastMsg(null), opts.durationMs || 4000);
  };

  const [retentionStep, setRetentionStep] = useState(null);
  const [onbFormOpen, setOnbFormOpen] = useState(false);
  const [paywall, setPaywall] = useState(null);
  const [usage, setUsage] = useState(null);
  const [unread, setUnread] = useState({ totalUnread: 0, latest: null });

  // Poll metered-usage status every 5 min. Drives the warning banner.
  useEffect(() => {
    if (!user) return;
    const tick = () => api('/api/billing/usage').then(setUsage).catch(()=>{});
    tick();
    const t = setInterval(tick, 5 * 60_000);
    return () => clearInterval(t);
  }, [user]);
  const [lastSeenAt, setLastSeenAt] = useState(() => { try { return localStorage.getItem('tradeops-last-msg-at') || ''; } catch { return ''; }});

  // Surface paywall errors from the api() helper as a top banner that links to Billing.
  useEffect(() => {
    const onPaywall = e => setPaywall(e.detail);
    const onNav = e => { if (e.detail?.tab) setTab(e.detail.tab); };
    // Session expired anywhere in the app → drop the user state so the Auth
    // shell takes over instead of leaving them on a half-loaded page full of
    // failed requests. Toast once so they know what happened.
    const onAuthExpired = () => {
      if (!user) return; // already logged out
      toast('Session expired — please log in again', 'warn');
      setUser(null);
    };
    // Super-admin tried a sub-account-level action while at agency level. Show an
    // in-app toast that says what to do, with a link that takes them there —
    // replaces the bare "No account context" browser alert.
    const onNoAccountContext = () => toast('Enter a sub-account first to do that', 'warn', {
      action: 'Open a sub-account', onAction: () => setTab('agency'), durationMs: 8000,
    });
    window.addEventListener('tradeops:paywall', onPaywall);
    window.addEventListener('tradeops:nav', onNav);
    window.addEventListener('tradeops:auth-expired', onAuthExpired);
    window.addEventListener('tradeops:no-account-context', onNoAccountContext);
    return () => {
      window.removeEventListener('tradeops:paywall', onPaywall);
      window.removeEventListener('tradeops:nav', onNav);
      window.removeEventListener('tradeops:auth-expired', onAuthExpired);
      window.removeEventListener('tradeops:no-account-context', onNoAccountContext);
    };
  }, [user]);

  // Poll for new inbound messages every 20s, toast when something new arrives.
  useEffect(() => {
    if (!user) return;
    const tick = async () => {
      try {
        const r = await api('/api/messaging/unread-counts');
        setUnread(r);
        if (r.latest?.at && r.latest.at !== lastSeenAt && lastSeenAt && tab !== 'messaging') {
          toast(`💬 New from ${r.latest.name || r.latest.phone}: ${r.latest.body}`, 'info');
        }
        if (r.latest?.at) {
          setLastSeenAt(r.latest.at);
          try { localStorage.setItem('tradeops-last-msg-at', r.latest.at); } catch {}
        }
      } catch {}
    };
    tick();
    const iv = setInterval(tick, 20000);
    return () => clearInterval(iv);
  }, [user, lastSeenAt, tab]);
  const logout = async () => {
    await api('/api/auth/logout', { method:'POST' });
    setUser(null); setTab('dashboard');
    try { require('./hooks/useTerminology.js').clearTerminology(); } catch {}
  };
  const interceptCancel = () => { setRetentionStep({ step:'reason' }); };

  const exitGhost = async () => {
    await api('/api/auth/unghost', { method:'POST' });
    window.location.reload();
  };

  // Post-signup guided wizard state — hooks MUST be above the early returns
  // below so React sees the same hook count on every render.
  const [wizardOpen, setWizardOpen] = React.useState(false);
  React.useEffect(() => {
    if (!user) return;
    if (user.role === 'super_admin' && !user.accountId) return;
    if (user.role === 'tech') return;
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('skipWizard')) return;
    api('/api/onboarding/progress').then(p => {
      if (p && !p.isComplete && (p.percent || 0) < 100) setWizardOpen(true);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!authReady) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:G.bg }}><Spinner sz={40}/></div>;
  if (!user) {
    if (wantsMarketing) return <MarketingLanding/>;
    return <>
      <Auth onLogin={u => { setUser(u); setTab(u.role === 'super_admin' && !u.accountId ? 'agency' : 'dashboard'); }} toast={toast} mode={initialPath === '/signup' ? 'signup' : 'login'}/>
      <Toast {...(toastMsg||{})} onClose={()=>setToastMsg(null)}/>
    </>;
  }

  // ─── Tech mobile shell ─────────────────────────────────────────────
  // Owners + admins can toggle "Preview as Tech" to see the exact mobile
  // view their techs get without logging in as a separate user. Persists
  // across reloads via localStorage. Real tech-role users always land here.
  const previewAsTech = (user.role === 'owner' || user.role === 'admin' || user.role === 'super_admin')
    && typeof window !== 'undefined' && localStorage.getItem('tradeops:previewAsTech') === '1';
  if (user.role === 'tech' || previewAsTech) {
    return <>
      <TechApp user={user} onLogout={logout} toast={toast} previewingAs={previewAsTech ? user.role : null}
        onExitPreview={previewAsTech ? () => { localStorage.removeItem('tradeops:previewAsTech'); window.location.reload(); } : null}/>
      <Toast {...(toastMsg||{})} onClose={()=>setToastMsg(null)}/>
    </>;
  }

  const isSuperAdmin = user.role === 'super_admin';
  const isGhosting = isSuperAdmin && user.accountId; // ghost mode: super_admin viewing a sub-account

  // Show the wizard full-screen when it's open (state managed above).
  if (wizardOpen && !isSuperAdmin) return <SetupWizard user={user} toast={toast} onFinished={() => setWizardOpen(false)}/>;

  const screen = (() => {
    const props = { user, toast, go: setTab, integrationStatus, refreshStatus: refreshIntegrationStatus };
    // Direct-link contact page — `/contact/:id` opens the full profile in a tab
    if (contactPageId) return <ContactProfile contactId={contactPageId} mode="page" toast={toast} onClose={()=>window.close()}/>;
    switch (tab) {
      case 'agency': return <Agency {...props}/>;
      case 'dashboard': return <Dashboard {...props}/>;
      case 'coach': return <Coach {...props}/>;
      case 'system-health': return <SystemHealth {...props}/>;
      case 'financials': return <Financials {...props}/>;
      case 'calendar': return <Calendar {...props}/>;
      case 'contacts': return <Contacts {...props}/>;
      case 'pipeline': return <Pipeline {...props}/>;
      case 'segments': return <Segments {...props}/>;
      case 'power-dialer': return <PowerDialer {...props}/>;
      case 'marketplace': return <LeadMarketplace {...props}/>;
      case 'team-chat': return <TeamChat {...props}/>;
      case 'jobs': return <Jobs {...props}/>;
      case 'estimates': return <Estimates {...props}/>;
      case 'invoices': return <Invoices {...props}/>;
      case 'documents': return <Documents {...props}/>;
      case 'pricebook': return <PriceBook {...props}/>;
      case 'memberships': return <Memberships {...props}/>;
      case 'forms': return <Forms {...props}/>;
      case 'messaging': return <Messaging {...props}/>;
      case 'workflows': return <WorkflowBuilder {...props}/>;
      case 'email-templates': return <EmailTemplates {...props}/>;
      case 'agents': return <Agents {...props}/>;
      case 'marketing': return <Marketing {...props}/>;
      case 'content': return <ContentStudio {...props}/>;
      case 'equipment': return <Equipment {...props}/>;
      case 'inventory': return <Inventory {...props}/>;
      case 'timesheets': return <Timesheets {...props}/>;
      case 'team': return <Team {...props}/>;
      case 'training': return <Training {...props}/>;
      case 'claude': return <ClaudeConnector {...props}/>;
      case 'settings': return <Settings {...props}/>;
      case 'integrations': return <Integrations {...props} status={integrationStatus}/>;
      case 'permits': return <Permits {...props}/>;
      case 'growth': return <Growth {...props}/>;
      case 'billing': return <Billing {...props}/>;
      case 'leadmap': return <LeadMap {...props}/>;
      case 'reviews': return <Reviews {...props}/>;
      case 'activity': return <ActivityLog {...props}/>;
      case 'jobsmap': return <JobsMap {...props}/>;
      case 'migration': return <Migration {...props}/>;
      case 'performance': return <Performance {...props}/>;
      case 'csm-portal': return <CsmPortal {...props}/>;
      case 'lead-sources': return <LeadSources {...props}/>;
      case 'marketing-overview': return <Marketing {...props} initialTab="campaigns"/>;
      case 'agency-pricing': return <AgencyPricing {...props}/>;
      case 'agency-branding': return <AgencyBranding {...props}/>;
      case 'rank-and-rent': return <RankAndRent {...props}/>;
      case 'inbox': return <AgencyInbox {...props}/>;
      case 'ai-credits': return <AiCredits {...props}/>;
      case 'website': return <WebsiteBuilder {...props}/>;
      case 'referrals': return <Referrals {...props}/>;
      case 'issues': return <Issues {...props}/>;
      default: return <Dashboard {...props}/>;
    }
  })();

  // Tab change on mobile auto-closes the sidebar so user can see content.
  // Also broadcasts tradeops:tab-change so any open overlays (contact profile, etc.)
  // dismiss themselves — otherwise the user clicks Marketing in the sidebar and
  // stays stuck behind the contact profile modal.
  const handleTabChange = (id) => {
    setTab(id);
    if (typeof window !== 'undefined' && window.innerWidth < 900) setSidebarOpen(false);
    try { window.dispatchEvent(new CustomEvent('tradeops:tab-change', { detail: { tab: id }})); } catch {}
  };
  // Wrap screen in an error boundary so a single component crash doesn't
  // turn the entire app into a black screen.
  const wrappedScreen = <ScreenErrorBoundary key={tab} tab={tab} onReset={()=>setTab('dashboard')}>{screen}</ScreenErrorBoundary>;

  return <div style={{ display:'flex', minHeight:'100vh', background:G.bg, position:'relative' }}>
    {/* Mobile sidebar overlay */}
    {sidebarOpen && typeof window !== 'undefined' && window.innerWidth < 900 && (
      <div onClick={()=>setSidebarOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:50 }}/>
    )}
    <div style={{
      // Desktop: always visible. Mobile: slide-in drawer.
      position: typeof window !== 'undefined' && window.innerWidth < 900 ? 'fixed' : 'relative',
      transform: typeof window !== 'undefined' && window.innerWidth < 900 ? (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
      transition: 'transform .25s ease',
      zIndex: 60,
      height: typeof window !== 'undefined' && window.innerWidth < 900 ? '100vh' : 'auto',
    }}>
      <Safe name="Sidebar"><Sidebar tab={tab} setTab={handleTabChange} user={user} integrationStatus={integrationStatus} isSuperAdmin={isSuperAdmin} features={features} unreadCount={unread.totalUnread}/></Safe>
    </div>
    <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>
      <Safe name="TopBar"><TopBar user={user} onLogout={logout} integrationStatus={integrationStatus} isGhosting={isGhosting} isSuperAdmin={isSuperAdmin} onOpenWizard={()=>setWizardOpen(true)} onExitGhost={exitGhost} toast={toast} onOpenOnboarding={()=>setOnbFormOpen(true)} onToggleSidebar={()=>setSidebarOpen(s=>!s)}/></Safe>
      {paywall && <div style={{ background:'#7c2d12', color:'#fff', padding:'10px 18px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:14 }}>
        <span style={{ fontSize:14 }}>🔒 {paywall.message}</span>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={()=>{ setTab('billing'); setPaywall(null); }} style={{ background:'#fff', color:'#7c2d12', border:'none', padding:'6px 14px', borderRadius:6, fontWeight:700, cursor:'pointer', fontSize:13 }}>Go to Billing →</button>
          <button onClick={()=>setPaywall(null)} style={{ background:'transparent', border:'1px solid #fff', color:'#fff', padding:'6px 10px', borderRadius:6, cursor:'pointer', fontSize:13 }}>Dismiss</button>
        </div>
      </div>}

      {/* Card-on-file required */}
      {usage && !usage.card?.onFile && (
        <div style={{ background:'#7c2d12', color:'#fff', padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <span style={{ fontSize:13, flex:1, minWidth:180 }}>💳 No card on file — AI calls + creatives paused. Overage will post on next invoice.</span>
          <button onClick={()=>setTab('billing')} style={{ background:'#fff', color:'#7c2d12', border:'none', padding:'6px 14px', borderRadius:6, fontWeight:700, cursor:'pointer', fontSize:13, flexShrink:0 }}>Add card →</button>
        </div>
      )}

      {/* Usage threshold warning */}
      {usage?.card?.onFile && usage?.overage?.approaching && (
        <div style={{ background:'#854d0e', color:'#fff', padding:'10px 18px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:14 }}>
          <span style={{ fontSize:14 }}>
            ⚠️ Usage at {usage.overage.pctUsed}% of your ${usage.overage.thresholdUsd} threshold (${usage.monthToDate.totalBilledUsd} so far this month). Charges over the threshold post on your next invoice.
          </span>
          <button onClick={()=>setTab('billing')} style={{ background:'#fff', color:'#854d0e', border:'none', padding:'6px 14px', borderRadius:6, fontWeight:700, cursor:'pointer', fontSize:13 }}>View usage →</button>
        </div>
      )}
      {usage?.overage?.exceeded && (
        <div style={{ background:'#9a3412', color:'#fff', padding:'10px 18px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:14 }}>
          <span style={{ fontSize:14 }}>
            🚨 Threshold exceeded — ${usage.monthToDate.totalBilledUsd} used vs ${usage.overage.thresholdUsd} cap. Overage will be billed on your next invoice.
          </span>
          <button onClick={()=>setTab('billing')} style={{ background:'#fff', color:'#9a3412', border:'none', padding:'6px 14px', borderRadius:6, fontWeight:700, cursor:'pointer', fontSize:13 }}>Raise threshold →</button>
        </div>
      )}
      <Safe name="SubTabBar"><SubTabBar tab={tab} setTab={handleTabChange} user={user} isSuperAdmin={isSuperAdmin} features={features}/></Safe>
      <div style={{ flex:1, overflow:'auto' }}>{wrappedScreen}</div>
    </div>
    <Toast {...(toastMsg||{})} onClose={()=>setToastMsg(null)}/>
    {/* Quick Create FAB — gives owners + admins the same one-tap estimate /
        invoice / payment / signature flow techs have, accessible from any tab. */}
    {user && user.role !== 'tech' && !isSuperAdmin && <QuickCreateFab toast={toast}/>}
    {/* Super-admin only sees the FAB while ghosting so it lands in the right account */}
    {isSuperAdmin && user.accountId && <QuickCreateFab toast={toast}/>}
    {/* Owner shell: Help bubble sits above the Quick Create FAB (FAB at bottom:24,
        56px tall, so Help sits at bottom:88). Owner shell uses right-side stacking. */}
    {/* Each auto-mounted overlay/widget gets its OWN micro-boundary so a
        crash in one (e.g. an unguarded .map on a not-yet-loaded array)
        doesn't blank the whole app via the root ErrorBoundary. */}
    <WidgetSafe name="HelpWidget"><HelpWidget currentPage={tab} bottomOffset={88}/></WidgetSafe>
    <WidgetSafe name="TrainingWidget"><TrainingWidget go={setTab}/></WidgetSafe>
    <WidgetSafe name="OnboardingWizard"><OnboardingWizard go={setTab} dismissed={onbDismissed} onDismiss={()=>setOnbDismissed(true)}/></WidgetSafe>
    <WidgetSafe name="FirstRunWizard"><FirstRunWizard go={setTab}/></WidgetSafe>
    <WidgetSafe name="OnboardingForm"><OnboardingForm open={onbFormOpen} onClose={()=>setOnbFormOpen(false)} onSaved={()=>{}} toast={toast}/></WidgetSafe>
    <WidgetSafe name="ContactQuickEditProvider"><ContactQuickEditProvider/></WidgetSafe>
    <WidgetSafe name="GlobalContactProfile"><GlobalContactProfile toast={toast}/></WidgetSafe>
    <WidgetSafe name="VoipDialerProvider"><VoipDialerProvider user={user} toast={toast}/></WidgetSafe>
    <WidgetSafe name="CmdK"><CmdK go={setTab}/></WidgetSafe>
  </div>;
}

// Visible-tab filter shared by Sidebar (domain unread badges) and SubTabBar.
// Defensive: returns [] when user is null so rapid tab/logout transitions
// don't crash the nav with "i is not a function" inside React's render.
function visibleTabs(user, isSuperAdmin, features) {
  if (!user) return [];
  // Leads-only tier — sub-accounts on plan 'leads_only' are marketplace buyers
  // who don't use the CRM/dispatch/finance side of TradeOps. They see only the
  // Get Leads dashboard, settings (to manage users), and integrations (Stripe).
  // Everything else is hidden.
  const isLeadsOnly = user?.account?.plan === 'leads_only' && !isSuperAdmin;
  if (isLeadsOnly) {
    const ALLOWED = new Set(['marketplace', 'settings', 'billing', 'integrations']);
    return NAV.filter(n => ALLOWED.has(n.id));
  }
  const TAB_PERM = {
    financials:'view_financials', invoices:'send_invoices', estimates:'create_estimates',
    contacts:'view_all_contacts', jobs:'view_all_jobs', pipeline:'view_all_jobs',
    messaging:'view_all_contacts', marketing:'view_marketing', team:'manage_team',
    integrations:'manage_integrations', settings:'modify_account_settings',
    billing:'manage_integrations', activity:'modify_account_settings',
    inventory:'view_inventory', workflows:'manage_workflows', content:'view_marketing',
  };
  const userPerms = Array.isArray(user?.permissions) ? user.permissions : [];
  const isOwnerish = user?.role === 'owner' || user?.role === 'super_admin';
  // Vertical-pack feature filter — if the account is a non-mobile vertical
  // (real estate, salon, vet, etc.), hide dispatch/jobs/tech-app tabs entirely
  // so the UI matches what they actually use. Map nav-tab id → vertical
  // feature flag.
  const vf = user?.account?.verticalFeatures || null;
  const VERT_GATE = {
    jobs: 'jobs', pipeline: 'jobs', calendar: 'jobs',
    dispatch: 'dispatch', leadmap: 'dispatch', jobsmap: 'dispatch',
    pricebook: 'pricebook', equipment: 'equipment', memberships: 'memberships',
    marketing: 'marketing', 'marketing-overview': 'marketing', content: 'marketing',
  };
  return NAV.filter(n => {
    if (n.superAdminOnly && !isSuperAdmin) return false;
    if (features && features[n.id] === false) return false;
    if (vf) {
      const vKey = VERT_GATE[n.id];
      if (vKey && vf[vKey] === false) return false;
    }
    const need = TAB_PERM[n.id];
    if (need && !isOwnerish && !userPerms.includes(need)) return false;
    return true;
  });
}

// Domain → first visible child tab (the "home" sub-tab for that domain).
function domainHome(domainId, tabs) {
  return tabs.find(t => t.domain === domainId)?.id;
}

function Sidebar({ tab, setTab, user, integrationStatus, isSuperAdmin, features, unreadCount = 0 }) {
  if (!user) return null; // logout/init transition — render nothing rather than crash
  const incomplete = integrationStatus && !(integrationStatus.stripe?.connected && integrationStatus.twilio?.connected);
  const tabs = visibleTabs(user, isSuperAdmin, features);
  const activeDomain = tabs.find(t => t.id === tab)?.domain;
  const goToDomain = (d) => {
    const home = domainHome(d, tabs);
    if (home) setTab(home);
  };
  const visibleDomains = DOMAINS.filter(d => {
    if (d.superAdminOnly && !isSuperAdmin) return false;
    return tabs.some(t => t.domain === d.id);
  });
  return <div style={{
    width:220, background:G.sidebar, borderRight:`1px solid ${G.border}`,
    display:'flex', flexDirection:'column', padding:'18px 0',
    height:'100vh', maxHeight:'100vh',
  }}>
    <div style={{ padding:'0 18px 14px 18px', borderBottom:`1px solid ${G.border}`, flexShrink:0 }}>
      {user.account?.brand?.logoUrl ? (
        <img src={user.account.brand.logoUrl} alt={user.account?.name || 'Logo'}
          onError={e => { e.currentTarget.style.display = 'none'; }}
          style={{ maxHeight:38, maxWidth:180, marginBottom:6, display:'block' }}/>
      ) : null}
      <div style={{ fontSize:17, fontWeight:800, color:G.text, letterSpacing:'-.01em' }}>{user.account?.name || (isSuperAdmin ? 'Agency Console' : 'TradeOps')}</div>
      <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>Powered by TradeOps</div>
    </div>
    <nav style={{ flex:1, padding:'14px 10px', overflowY:'auto', minHeight:0, WebkitOverflowScrolling:'touch' }}>
      {visibleDomains.map(d => {
        const active = activeDomain === d.id;
        const showUnread = d.id === 'conversations' && unreadCount > 0;
        const showWarning = d.id === 'settings' && incomplete;
        return <div key={d.id} onClick={()=>goToDomain(d.id)} style={{
          padding:'10px 14px', borderRadius:8, marginBottom:2, cursor:'pointer',
          display:'flex', alignItems:'center', gap:11, fontSize:14, fontWeight:600,
          background: active ? G.accentDim : 'transparent',
          color: active ? G.accent : G.text,
          borderLeft: active ? `3px solid ${G.accent}` : '3px solid transparent',
          paddingLeft: active ? 11 : 14,
        }}>
          <span style={{ fontSize:17, width:20, textAlign:'center' }}>{d.icon}</span>
          <span>{d.label}</span>
          {showUnread && <span style={{ marginLeft:'auto', background:G.red, color:'#fff', fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:99 }}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
          {showWarning && <span style={{ marginLeft:'auto' }}><Dot c={G.yellow} pulse/></span>}
        </div>;
      })}
    </nav>
    <div style={{ padding:'10px 16px', fontSize:11, color:G.textDim, borderTop:`1px solid ${G.border}` }}>
      {user.email}
    </div>
  </div>;
}

// Horizontal sub-tab strip rendered at the top of the active domain. Hidden
// when the domain has only one sub-tab (nothing to choose).
function SubTabBar({ tab, setTab, user, isSuperAdmin, features }) {
  if (!user || !tab) return null;
  const tabs = visibleTabs(user, isSuperAdmin, features);
  if (!tabs.length) return null;
  const activeDomain = tabs.find(t => t.id === tab)?.domain;
  if (!activeDomain) return null;
  const children = tabs.filter(t => t.domain === activeDomain);
  if (children.length < 2) return null;
  const domainLabel = DOMAINS.find(d => d.id === activeDomain)?.label || '';
  return <div data-tabstrip style={{
    display:'flex', alignItems:'center', gap:6, padding:'14px 24px 0',
    borderBottom:`1px solid ${G.border}`, background:G.bg,
    overflowX:'auto', whiteSpace:'nowrap', WebkitOverflowScrolling:'touch',
  }}>
    <h2 style={{ margin:'0 18px 0 0', fontSize:18, fontWeight:800, color:G.text, flexShrink:0 }}>{domainLabel}</h2>
    {children.map(c => {
      const active = c.id === tab;
      return <div key={c.id} onClick={()=>setTab(c.id)} style={{
        padding:'8px 14px 11px', cursor:'pointer', fontSize:13, fontWeight:600,
        color: active ? G.accent : G.textMuted,
        borderBottom: active ? `2px solid ${G.accent}` : '2px solid transparent',
        marginBottom:-1, flexShrink:0,
      }}>{c.label}</div>;
    })}
  </div>;
}

// Live MTD margin pill — visible only when super-admin is ghosting a sub-account.
// Pulls /api/agency/accounts/:id/margin every 60s.
function GhostMarginBadge({ accountId }) {
  const [m, setM] = React.useState(null);
  React.useEffect(() => {
    if (!accountId) return;
    let cancel = false;
    const load = () => api(`/api/agency/accounts/${accountId}/margin`)
      .then(d => !cancel && setM(d.monthToDate))
      .catch(()=>{});
    load();
    const t = setInterval(load, 60000);
    return () => { cancel = true; clearInterval(t); };
  }, [accountId]);
  if (!m) return null;
  const color = m.marginPct >= 50 ? G.green : m.marginPct >= 20 ? G.yellow : G.red;
  return <Badge c={color} bg={color+'22'} title={`Cost $${m.ourCostUsd} · Billed $${m.billedUsd}`} style={{ flexShrink:0, fontFamily:'monospace' }}>
    💰 ${m.marginUsd} ({m.marginPct}%)
  </Badge>;
}

function TopBar({ user, onLogout, integrationStatus, isGhosting, isSuperAdmin, onOpenWizard, onExitGhost, toast, onOpenOnboarding, onToggleSidebar }) {
  const [smsEnabled, setSmsEnabled] = useState(null);
  const [voiceEnabled, setVoiceEnabled] = useState(null);
  const accountPhone = integrationStatus?.twilio?.phone;
  useEffect(() => {
    api('/api/messaging/playbook').then(p => {
      // Server returns either smsEnabled (new) or chatEnabled (legacy alias)
      setSmsEnabled(p.smsEnabled !== undefined ? p.smsEnabled : p.chatEnabled);
      setVoiceEnabled(p.voiceEnabled);
    }).catch(()=>{});
  }, []);
  const toggleSms = async () => {
    const next = !smsEnabled;
    try { await api('/api/messaging/playbook', { method:'PATCH', body:{ smsEnabled: next }}); setSmsEnabled(next); toast(next?'💬 AI SMS ON':'💬 AI SMS OFF'); }
    catch(e) { toast(e.message,'error'); }
  };
  const toggleVoice = async () => {
    const next = !voiceEnabled;
    try { await api('/api/messaging/playbook', { method:'PATCH', body:{ voiceEnabled: next }}); setVoiceEnabled(next); toast(next?'📞 AI Voice ON — provisioning agent…':'📞 AI Voice OFF'); }
    catch(e) { toast(e.message,'error'); }
  };

  // Detect mobile viewport for layout decisions
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 900;
  const [menuOpen, setMenuOpen] = useState(false);
  // Theme toggle — local mirror of the persisted theme so the icon flips
  // without rerendering the whole TopBar tree.
  const [theme, setThemeState] = useState(() => {
    if (typeof localStorage === 'undefined') return 'dark';
    try { return localStorage.getItem('tradeops_theme') === 'light' ? 'light' : 'dark'; } catch { return 'dark'; }
  });
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setThemeState(next);
    import('./theme.js').then(m => m.applyTheme(next));
  };

  return <div style={{ height:56, padding:'0 12px', borderBottom:`1px solid ${G.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', background:G.sidebar, gap:8, overflow:'hidden' }}>
    <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0, flex:1 }}>
      {/* Hamburger — always shown on mobile */}
      <button onClick={onToggleSidebar} className="mobile-hamburger" style={{
        display:'none', background:'none', border:'none', color:G.text, fontSize:24, cursor:'pointer', padding:6, flexShrink:0,
      }} title="Menu">☰</button>

      {/* Ghosting badge — compact on mobile. Now also fetches + shows live
          MTD margin for super-admin so they always know what the client is
          costing vs paying. */}
      {isGhosting && (
        <Badge c={G.orange} bg={G.orangeLight} style={{ flexShrink:0 }}>
          👻 {isMobile ? '' : 'Viewing '}{(user.account?.name||'').slice(0, isMobile ? 12 : 30)} · <span onClick={onExitGhost} style={{ cursor:'pointer', textDecoration:'underline', marginLeft:4 }}>Exit</span>
        </Badge>
      )}
      {isGhosting && <GhostMarginBadge accountId={user.account?.id}/>}

      {/* Setup progress pill — shows until the owner hits 100% completion
          on the SetupWizard. Click to reopen the wizard. Hidden for the
          agency plane (super-admin isn't the one being onboarded). */}
      {!isSuperAdmin && !isGhosting && <SetupCompletenessPill onResume={onOpenWizard}/>}

      {/* Desktop-only badges: phone + stripe + twilio + SMS/Voice toggles */}
      {!isMobile && <>
        {accountPhone && !isGhosting && (
          <Badge c={G.cyan} bg={G.cyan+'22'} title="Your business phone (SMS + calls)">📞 {accountPhone}</Badge>
        )}
        {integrationStatus && !isGhosting && <>
          <Badge c={integrationStatus.stripe?.connected?G.green:G.textMuted} bg={(integrationStatus.stripe?.connected?G.green:G.textMuted)+'22'}><Dot c={integrationStatus.stripe?.connected?G.green:G.textMuted}/> Stripe</Badge>
          <Badge c={integrationStatus.twilio?.connected?G.green:G.textMuted} bg={(integrationStatus.twilio?.connected?G.green:G.textMuted)+'22'}><Dot c={integrationStatus.twilio?.connected?G.green:G.textMuted}/> Twilio</Badge>
        </>}
        <div style={{ display:'flex', gap:6 }}>
          {smsEnabled !== null && <div onClick={toggleSms} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', borderRadius:18, cursor:'pointer', background: smsEnabled?G.greenLight:G.input, border:`1px solid ${smsEnabled?G.green+'66':G.cardBorder}`, color: smsEnabled?G.green:G.textMuted, fontSize:11, fontWeight:700 }} title="Master AI SMS auto-reply">
            <span style={{ fontSize:13 }}>💬</span><span>SMS {smsEnabled?'ON':'OFF'}</span>
          </div>}
          {voiceEnabled !== null && <div onClick={toggleVoice} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', borderRadius:18, cursor:'pointer', background: voiceEnabled?G.greenLight:G.input, border:`1px solid ${voiceEnabled?G.green+'66':G.cardBorder}`, color: voiceEnabled?G.green:G.textMuted, fontSize:11, fontWeight:700 }} title="Master AI Voice">
            <span style={{ fontSize:13 }}>📞</span><span>Voice {voiceEnabled?'ON':'OFF'}</span>
          </div>}
        </div>
      </>}
    </div>

    <div style={{ display:'flex', alignItems:'center', gap:isMobile?8:14, flexShrink:0 }}>
      <button onClick={toggleTheme} title={`Switch to ${theme==='dark'?'light':'dark'} mode`} style={{
        background:'transparent', border:'none', color:G.text, fontSize:18, cursor:'pointer', padding:6,
      }}>{theme==='dark' ? '☀️' : '🌙'}</button>
      <NotificationBell toast={toast}/>
      {!isMobile && <>
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:13, fontWeight:600 }}>{user.firstName} {user.lastName}</div>
          <div style={{ fontSize:11, color:G.textMuted }}>{user.role}</div>
        </div>
        <Btn sz="sm" onClick={onOpenOnboarding}>Setup</Btn>
        {(user.role === 'owner' || user.role === 'admin' || user.role === 'super_admin') &&
          <Btn sz="sm" v="ghost" onClick={()=>{ localStorage.setItem('tradeops:previewAsTech', '1'); window.location.reload(); }} title="See exactly what your field techs see">View as tech</Btn>}
      </>}
      <Av name={`${user.firstName||''} ${user.lastName||''}`}/>
      {!isMobile && <Btn sz="sm" onClick={onLogout}>Logout</Btn>}

      {/* Mobile: overflow menu replaces Setup + Logout */}
      {isMobile && (
        <div style={{ position:'relative' }}>
          <button onClick={()=>setMenuOpen(o=>!o)} style={{ background:'none', border:'none', color:G.text, fontSize:20, cursor:'pointer', padding:6 }}>⋮</button>
          {menuOpen && <>
            <div onClick={()=>setMenuOpen(false)} style={{ position:'fixed', inset:0, zIndex:90 }}/>
            <div style={{ position:'absolute', right:0, top:36, background:G.card, border:`1px solid ${G.cardBorder}`, borderRadius:10, padding:6, zIndex:91, minWidth:180, boxShadow:'0 12px 32px rgba(0,0,0,0.5)' }}>
              <div style={{ padding:'8px 12px', borderBottom:`1px solid ${G.cardBorder}`, marginBottom:4 }}>
                <div style={{ fontSize:13, fontWeight:600 }}>{user.firstName} {user.lastName}</div>
                <div style={{ fontSize:11, color:G.textMuted }}>{user.role}</div>
              </div>
              {smsEnabled !== null && <button onClick={()=>{ toggleSms(); setMenuOpen(false); }} style={mobMenuItem}>SMS {smsEnabled?'ON':'OFF'}</button>}
              {voiceEnabled !== null && <button onClick={()=>{ toggleVoice(); setMenuOpen(false); }} style={mobMenuItem}>Voice {voiceEnabled?'ON':'OFF'}</button>}
              <button onClick={()=>{ onOpenOnboarding(); setMenuOpen(false); }} style={mobMenuItem}>Setup</button>
              {(user.role === 'owner' || user.role === 'admin' || user.role === 'super_admin') &&
                <button onClick={()=>{ localStorage.setItem('tradeops:previewAsTech', '1'); window.location.reload(); }} style={mobMenuItem}>View as tech</button>}
              <button onClick={()=>{ onLogout(); setMenuOpen(false); }} style={{...mobMenuItem, color:'#ef4444'}}>Logout</button>
            </div>
          </>}
        </div>
      )}
    </div>
  </div>;
}

const mobMenuItem = {
  display:'block', width:'100%', textAlign:'left', background:'none', border:'none',
  padding:'10px 12px', cursor:'pointer', color:G.text, fontSize:13, borderRadius:6,
};

function NotificationBell({ toast }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [unread, setUnread] = useState(0);
  const [coords, setCoords] = useState({ top: 0, right: 16 });
  const bellRef = React.useRef(null);
  const load = () => api('/api/messaging/notifications').then(r => { setEvents(r.events||[]); setUnread(r.unreadCount||0); }).catch(()=>{});
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const markRead = async () => {
    try { await api('/api/messaging/notifications/mark-read', { method:'POST' }); setUnread(0); }
    catch {}
  };
  // Position the dropdown anchored to the bell, but rendered via portal at
  // document.body so it can't be clipped by any topbar overflow / transform.
  const toggle = () => {
    const next = !open;
    if (next && bellRef.current) {
      const r = bellRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 6, right: Math.max(12, window.innerWidth - r.right) });
    }
    setOpen(next);
    if (next && unread > 0) markRead();
  };
  const click = (ev) => {
    try { window.dispatchEvent(new CustomEvent('tradeops:nav', { detail: { tab: ev.link || 'messaging' }})); } catch {}
    if (ev.contactId) {
      try { window.dispatchEvent(new CustomEvent('tradeops:edit-contact', { detail: { id: ev.contactId }})); } catch {}
    }
    setOpen(false);
  };
  const timeAgo = (iso) => {
    if (!iso) return '';
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s/60)}m`;
    if (s < 86400) return `${Math.floor(s/3600)}h`;
    return `${Math.floor(s/86400)}d`;
  };
  const portal = open && typeof document !== 'undefined' ? ReactDOM.createPortal(
    <>
      <div onClick={()=>setOpen(false)} style={{ position:'fixed', inset:0, zIndex:9990, background:'transparent' }}/>
      <div style={{ position:'fixed', top:coords.top, right:coords.right, width:380, maxHeight:'min(500px, 70vh)', overflowY:'auto', background:G.card, border:`1px solid ${G.cardBorder}`, borderRadius:12, boxShadow:'0 12px 40px rgba(0,0,0,0.45)', zIndex:9991 }}>
        <div style={{ padding:'12px 14px', borderBottom:`1px solid ${G.cardBorder}`, display:'flex', justifyContent:'space-between', alignItems:'center', background:G.card, position:'sticky', top:0 }}>
          <strong style={{ fontSize:14 }}>Inbox</strong>
          <span style={{ fontSize:11, color:G.textMuted }}>{events.length} recent</span>
        </div>
        {events.length === 0 ? (
          <div style={{ padding:30, textAlign:'center', color:G.textMuted, fontSize:13 }}>No inbound activity yet.</div>
        ) : events.map((ev, i) => (
          <div key={i} onClick={()=>click(ev)} style={{ padding:'12px 14px', borderBottom:`1px solid ${G.cardBorder}55`, cursor:'pointer', background: ev.unread ? G.purple+'11' : 'transparent' }}>
            <div style={{ display:'flex', gap:10 }}>
              <div style={{ fontSize:18, flexShrink:0 }}>{ev.icon}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, display:'flex', justifyContent:'space-between', gap:8 }}>
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ev.title}</span>
                  <span style={{ fontSize:11, color:G.textMuted, flexShrink:0 }}>{timeAgo(ev.at)}</span>
                </div>
                {ev.body && <div style={{ fontSize:12, color:G.textMuted, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>{ev.body}</div>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>,
    document.body
  ) : null;
  return <div ref={bellRef} style={{ position:'relative' }}>
    <div onClick={toggle} style={{ cursor:'pointer', position:'relative', padding:'6px 8px', borderRadius:8 }} title="Notifications">
      <span style={{ fontSize:20 }}>🔔</span>
      {unread > 0 && <span style={{ position:'absolute', top:0, right:0, background:G.red||'#ef4444', color:'#fff', borderRadius:99, padding:'1px 6px', fontSize:10, fontWeight:800, minWidth:16, textAlign:'center' }}>{unread > 9 ? '9+' : unread}</span>}
    </div>
    {portal}
  </div>;
}

// React error boundary — wraps every screen so a single component crash
// shows a clear error card instead of blanking the whole app. We key by tab
// so switching tabs resets the boundary state.
// Micro-boundary for auto-mounted widgets. When a widget throws, it's
// silently swallowed (logged + posted to /api/client-errors) and the rest
// of the app keeps running. Use sparingly — only for non-essential overlays
// where rendering nothing is better than crashing.
class WidgetSafe extends React.Component {
  state = { broken: false };
  static getDerivedStateFromError() { return { broken: true }; }
  componentDidCatch(err, info) {
    console.error(`[WidgetSafe:${this.props.name}]`, err, info?.componentStack);
    try {
      fetch('/api/client-errors', {
        method:'POST', credentials:'include',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({
          widget: this.props.name,
          message: String(err?.message || err),
          stack: String(err?.stack || ''),
          componentStack: info?.componentStack || '',
          ua: navigator.userAgent, url: window.location.href,
        }),
      }).catch(()=>{});
    } catch {}
  }
  render() { return this.state.broken ? null : this.props.children; }
}

class ScreenErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { error: null, info: null, copied: false }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error('[ScreenErrorBoundary]', error, info?.componentStack);
    this.setState({ info: info?.componentStack || null });
    // Best-effort POST to server so we have a record of crashes without
    // relying on the user copying it manually.
    try {
      fetch('/api/client-errors', {
        method:'POST', credentials:'include',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({
          tab: this.props.tab,
          message: String(error?.message || error),
          stack: String(error?.stack || ''),
          componentStack: info?.componentStack || '',
          ua: navigator.userAgent,
          url: window.location.href,
        }),
      }).catch(()=>{});
    } catch {}
  }
  copy = () => {
    const text = `Tab: ${this.props.tab}\n${this.state.error?.stack || this.state.error || ''}\n\nComponent stack:${this.state.info || ''}`;
    try { navigator.clipboard.writeText(text); this.setState({ copied: true }); setTimeout(()=>this.setState({copied:false}), 2000); } catch {}
  };
  render() {
    if (this.state.error) {
      return <div style={{ padding:40 }}>
        <div style={{ background:G.card, border:`1px solid ${G.cardBorder}`, borderRadius:14, padding:40, textAlign:'center', maxWidth:680, margin:'0 auto' }}>
          <div style={{ fontSize:48 }}>⚠️</div>
          <h2 style={{ margin:'14px 0 6px', color:G.text }}>Something broke on this screen</h2>
          <div style={{ color:G.textMuted, fontSize:13, marginBottom:18 }}>
            The "{this.props.tab}" tab crashed. Other tabs still work. Hit <strong>📋 Copy error</strong> and paste it back so we can fix it fast.
          </div>
          <pre style={{ background:G.input, padding:14, borderRadius:8, fontSize:11, color:G.textMuted, textAlign:'left', overflow:'auto', maxHeight:220, whiteSpace:'pre-wrap' }}>
{String(this.state.error?.stack || this.state.error || 'Unknown error')}
{this.state.info ? `\n\nComponent stack:${this.state.info}` : ''}
          </pre>
          <div style={{ marginTop:18, display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
            <button onClick={this.copy} style={{ padding:'10px 18px', borderRadius:8, border:`1px solid ${G.cardBorder}`, background:this.state.copied?G.greenLight:G.input, color:this.state.copied?G.green:G.text, cursor:'pointer', fontWeight:600 }}>{this.state.copied?'✓ Copied':'📋 Copy error'}</button>
            <button onClick={()=>this.setState({error:null,info:null})} style={{ padding:'10px 18px', borderRadius:8, border:`1px solid ${G.cardBorder}`, background:G.input, color:G.text, cursor:'pointer', fontWeight:600 }}>Retry</button>
            <button onClick={this.props.onReset} style={{ padding:'10px 18px', borderRadius:8, border:'none', background:G.purple, color:'#fff', cursor:'pointer', fontWeight:700 }}>Go to Dashboard</button>
          </div>
        </div>
      </div>;
    }
    return this.props.children;
  }
}
